import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';
import { IRoom } from 'types/room';
import ChatsContext from 'contexts/chats/context';
import { ContactSelected } from 'contexts/conversation/provider';
import AuthContext from 'contexts/auth/context';
import useEnvironment from '../../hooks/useEnvironments';
import MenuChatsContext from '../../contexts/menuChats/context';
import DrawerContext from '../../contexts/drawer/context';
import ConversationContext from '../../contexts/conversation/context';

const useKebabOptions = (itemSelected: IRoom | undefined, handlePopoverKebab: any, kebabName: string) => {
  const theme = useTheme();
  const { getBasePath } = useEnvironment();
  const [domain, setDomain] = useState('');
  const [origin, setOrigin] = useState('');
  const [showTooltipCopied, setShowTooltipCopied] = useState(false);
  const [openOnHoldModal, setOpenOnHoldModal] = useState(false);
  const [options, setOptions] = useState([] as any);
  const { t } = useTranslation();
  const { setOpenDrawerContactManager } = useContext(MenuChatsContext);
  const { handleStartNewConversation, handleSelectContact } = useContext(ConversationContext);
  const { loadUserInfo } = useContext(DrawerContext);
  const { setServiceSelected } = useContext(ChatsContext);
  const { currentUserInfo, allowsManualOnHold } = useContext(AuthContext);

  const chatOptions = [
    {
      type: 'expandChat',
      icon: itemSelected?.open ? (
        <i
          className="fa-regular fa-arrow-up-right-from-square"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ) : (
        <i
          className="fa-solid fa-up-right-and-down-left-from-center fa-2xl"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ),
      title: itemSelected?.open ? t('seeInQueue') : t('expandChat'),
      function: () => {
        handlePopoverKebab();
        window.open(`${domain}/${itemSelected?._id}`, '_blank')?.focus();
      },
    },
    {
      type: 'copyLink',
      icon: (
        <i
          className="fa-regular fa-link"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ),
      title: t('copyLink'),
      function: () => {
        navigator.clipboard.writeText(`${origin}${domain}/${itemSelected?._id}`);
        setShowTooltipCopied(true);
        setTimeout(() => {
          setShowTooltipCopied(false);
          handlePopoverKebab();
        }, 2000);
      },
    },
    ...(allowsManualOnHold && !itemSelected?.closedAt && itemSelected?.servedBy?._id === currentUserInfo._id
      ? [
          {
            type: 'onHold',
            icon: (
              <i
                className="fa-regular fa-circle-pause fa-l"
                style={{
                  color: theme.palette.neutral.var80,
                }}
              />
            ),
            title: itemSelected?.onHold ? t('resumeOnHold') : t('putChatOnHold'),
            function: () => {
              handlePopoverKebab();
              setServiceSelected(itemSelected);
              setOpenOnHoldModal(true);
            },
          },
        ]
      : []),
    {
      type: 'history',
      icon: (
        <i
          className="fa-regular fa-clock-rotate-left fa-l"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ),
      title: t('chatHistory'),
      function: () => {
        handlePopoverKebab();
        window.location.replace(`${domain}/${itemSelected?._id}?history=true`);
      },
    },
  ];

  const contactManagerOptions = [
    {
      type: 'startNewConversation',
      icon: (
        <i
          className="fa-regular fa-comment-plus"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ),
      title: t('startNewConversation'),
      function: () => {
        handleSelectContact(itemSelected as unknown as ContactSelected);
        handleStartNewConversation(true);
      },
    },
    {
      type: 'editInformation',
      icon: (
        <i
          className="fa-regular fa-pen"
          style={{
            color: theme.palette.neutral.var80,
          }}
        />
      ),
      title: t('editInformation'),
      function: () => {
        loadUserInfo(itemSelected?._id);
        setOpenDrawerContactManager(true);
        handlePopoverKebab();
      },
    },
  ];

  const handleCloseOnHoldModal = () => {
    setOpenOnHoldModal(false);
  };

  useEffect(() => {
    if (kebabName === 'chatsManager') {
      setOptions(chatOptions);
      return;
    }

    setOptions(contactManagerOptions);
  }, [kebabName, itemSelected, domain, origin]);

  useEffect(() => {
    const path = getBasePath();
    setDomain(path);
    setOrigin(window.location.origin);
  }, []);

  return {
    options,
    translation: t,
    showTooltipCopied,
    openOnHoldModal,
    handleCloseOnHoldModal,
  };
};

export default useKebabOptions;
